"use client";

import { useLocale, useTranslations } from "next-intl";
import { AvatarStack } from "@/components/ui/avatar";
import { AI_ASSISTANT_NAME, resolveAiName } from "@/lib/ai-assistant";
import type { HomeBlock, WidgetConfig, WidgetResponseTimeBucket } from "@/lib/widget-config";

/** Per-bucket i18n key — the BE returns the enum, the widget owns the copy. */
const REPLY_TIME_KEY = {
  INSTANT: "teamStatusReplyInstant",
  MINUTES: "teamStatusReplyMinutes",
  HOURS: "teamStatusReplyHours",
  DAY: "teamStatusReplyDay",
} as const satisfies Record<WidgetResponseTimeBucket, string>;

const RESPONSE_BUCKETS = Object.keys(REPLY_TIME_KEY) as WidgetResponseTimeBucket[];

/** Format the next-open instant in the visitor's locale/tz, e.g. "Monday, 9:00 AM".
 *  Returns `null` on a missing/unparseable value so callers fall back to the
 *  generic "back soon" copy. */
function formatNextOpen(iso: string | null | undefined, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: "long",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return null;
  }
}

/**
 * Home `team-status` block — presence-aware (BE-computed `presence` /
 * `responseTime` / `nextOpenAt` injected onto the block):
 *   - HUMAN   → green dot + "We're online" (+ optional reply-time line);
 *   - AI      → green dot + "operators away, but {AI} can help" (+ optional back-time);
 *   - OFFLINE → gray dot + "we're away, back {when}" (or generic "back soon").
 * Absent presence (demo / older config) reads as HUMAN.
 */
export function TeamStatusBlock({
  block,
  config,
}: {
  block: Extract<HomeBlock, { kind: "team-status" }>;
  config: WidgetConfig;
}) {
  const t = useTranslations("home");
  const locale = useLocale();

  const presence = block.presence ?? "HUMAN";
  // Dot colour per presence: human → green, AI-available → amber (operators
  // away but the agent can answer), fully offline → grey.
  const dotClass =
    presence === "OFFLINE"
      ? "bg-wx-fg-subtle"
      : presence === "AI"
        ? "bg-wx-warning"
        : "bg-wx-success";
  const whenOpen = formatNextOpen(block.nextOpenAt, locale);

  let primary: string;
  let secondary: string | null = null;

  if (presence === "AI") {
    // Human operators are away but AI auto-reply can field common questions.
    const aiName = resolveAiName(config.messenger?.aiAssistant) ?? AI_ASSISTANT_NAME;
    primary = t("teamStatusAiOnline", { name: aiName });
    secondary = whenOpen ? t("teamStatusOperatorsBack", { when: whenOpen }) : null;
  } else if (presence === "OFFLINE") {
    primary = whenOpen
      ? t("teamStatusOfflineUntil", { when: whenOpen })
      : t("teamStatusOfflineSoon");
  } else {
    // HUMAN — online; show the reply-time line only when the operator's block
    // toggle is on AND the BE supplied a valid bucket.
    primary = t("teamStatusOnline");
    const bucket =
      block.showResponseTime && block.responseTime && RESPONSE_BUCKETS.includes(block.responseTime)
        ? block.responseTime
        : null;
    secondary = bucket ? t(REPLY_TIME_KEY[bucket]) : null;
  }

  return (
    <div className="flex items-center gap-3 rounded-wx-lg bg-wx-bg-elevated px-3.5 py-3">
      {/* Dot + text in their OWN row so the dot aligns to the title's first
          line — not the taller avatar row. With the avatar the parent row is
          ~avatar-height; keeping the dot's offset relative to this text-only
          flex stops it drifting above the title. */}
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          aria-hidden="true"
          // Centred on the first line: the title is text-sm (20px line), the
          // dot 8px, so 6px down puts it level with the title's middle rather
          // than hanging above it.
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotClass}`}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-wx-fg">{primary}</p>
          {secondary && <p className="text-xs text-wx-fg-muted">{secondary}</p>}
        </div>
      </div>
      {block.showOperatorAvatars && config.operatorAvatars.length > 0 && (
        <AvatarStack avatars={config.operatorAvatars} size="sm" ringClass="ring-wx-bg-elevated" />
      )}
    </div>
  );
}
